'use client'
import React, { useLayoutEffect, useMemo, useRef } from 'react';
import classNames from "classnames";
import { cloneDeep } from 'lodash';
import chroma from "chroma-js";

import { Map } from "@/migrated-widgets/table-map/components/molecules/Map";
import ShapeMarker from "@/migrated-widgets/table-map/components/molecules/Map/ShapeMarker";
import { SpinnerBox } from "@/migrated-widgets/table-map/components/atoms/Spinner/SpinnerBox";
import { useAudienceVisitorsTableMapState } from "./AudienceVisitorsTableMapState";

import { MapSideProps } from "./types";
import TagCircleMarker from '@/migrated-widgets/table-map/components/molecules/Map/TagCircleMarker';
import { Environments } from '@/migrated-widgets/table-map/constants/Environments';
import { DealerPinIcon } from '@/migrated-widgets/table-map/components/icons/DealerPinIcon';
import { MapContextType, Feature } from '@/migrated-widgets/table-map/components/molecules/Map/types';
import { productColors, locationProducts } from '@/migrated-widgets/table-map/components/organisms/TableMap/constants';


const MapSide: React.FC<MapSideProps> = () => {
    const {
        setZipCodes,
        loadingAudiences,
        loadingDealers,
        loadingLocations,
        audienceMarkers,
        dealerMarkers,
        circleMarkers,
        geometryFeatures,
        resetZoom,
        setResetZoom
    } = useAudienceVisitorsTableMapState();
    const mapProvider = useRef<MapContextType>(null);

    const handleSelectZipCode = (zipCode: string | undefined) => {
        if (!zipCode) return
        setZipCodes([zipCode]);
    }

    const renderedCircleMarkers = useMemo(() => {
        const markers: any[] = [];
        [...circleMarkers]
            .sort((a, b) => b.radius - a.radius)
            .forEach((marker) => {
                for (const location of marker.locations) {
                    const color = productColors[location.product] ?? '';
                    const productName = locationProducts.find((locationProduct) => locationProduct.value === location.product)?.name;
                    if (productName) {
                        markers.push(
                            <ShapeMarker
                                label={`<strong>Product:</strong> ${productName}<br>${marker.properties.label}`}
                                strokeColor={chroma.valid(color) ? chroma(color).darken().hex() : 'gray'}
                                strokeOpacity={0.625}
                                strokeWeight={1.25}
                                fillColor={chroma.valid(color) ? color : 'gray'}
                                fillOpacity={0.2}
                                zIndex={2}
                                radius={marker.radius}
                                shape="circle"
                                key={`location_${location.product}_${marker.position.lat}_${marker.position.lng}_${marker.radius}`}
                                center={marker.position}
                            />,
                        )
                    }
                    break;
                }
            })
        return markers;
    }, [circleMarkers]);

    const renderedGeometryFeatures = useMemo((): Feature[] => {
        const features: Feature[] = [];
        geometryFeatures.forEach((feature) => {
            for (const location of feature.locations) {
                const color = productColors[location.product] ?? '';
                const feat = cloneDeep(feature);
                const productName = locationProducts.find((locationProduct) => locationProduct.value === location.product)?.name ?? '';
                feat['properties']['label'] = `<strong>Product:</strong> ${productName}<br>${feat['properties']['label']}`;
                feat['properties']['strokeColor'] = chroma.valid(color) ? chroma(color).darken().hex() : 'gray';
                feat['properties']['strokeOpacity'] = 0.625;
                feat['properties']['strokeWeight'] = 1.25;
                feat['properties']['fillColor'] = chroma.valid(color) ? color : 'gray';
                feat['properties']['fillOpacity'] = 0.2;
                feat['properties']['zIndex'] = 4;
                features.push(feat);
                break;
            }
        })
        return features;
    }, [geometryFeatures]);

    useLayoutEffect(() => {
        if (!dealerMarkers.length || !mapProvider.current?.map || !resetZoom) return;

        const positions: google.maps.LatLngLiteral[] = dealerMarkers.map((d) => d.position);

        mapProvider.current.centerBy(positions);

        google.maps.event.addListenerOnce(mapProvider.current.map, 'idle', () => {
            const zoom = mapProvider.current?.map?.getZoom() ?? 0;
            if (zoom > 11) mapProvider.current?.map?.setZoom(11);
        });

        setResetZoom(false);
    }, [dealerMarkers, mapProvider.current?.map, resetZoom])

    const stopDragPropagation = (e: React.PointerEvent | React.MouseEvent | React.TouchEvent) => {
        e.stopPropagation();
    };

    return (
        <div
            className={classNames('w-full', 'h-full')}
            onPointerDown={stopDragPropagation}
            onMouseDown={stopDragPropagation}
            onTouchStart={stopDragPropagation}
        >
            {(loadingAudiences || loadingDealers || loadingLocations) && <SpinnerBox boxBg="white" color="primary" size={5} boxClassName={classNames('bg-opacity-80')} />}
            <Map
                mapTypeId="terrain"
                mapTypeControl={false}
                streetViewControl={false}
                mapId={Environments.TABLE_MAP_WIDGET_MAP_ID}
                data={renderedGeometryFeatures}
                maxZoom={16}
                zoom={12}
                centerByTags={false}
                ref={mapProvider}
            >
                {
                    renderedCircleMarkers
                }
                {
                    audienceMarkers.map((marker, index) => (
                        <TagCircleMarker
                            key={`zipcode_${marker.zipCode}_${marker.position.lat}_${marker.position.lng}_${index}`}
                            label={`<div style="text-align:center;">Zip Code<br><strong>${marker.zipCode}</strong></div>`}
                            onClick={() => handleSelectZipCode(marker.zipCode)}
                            zIndex={8}
                            position={marker.position}>
                            <div className={classNames(
                                "z-[999] rounded-full flex items-center justify-center marker-zoomIn-animation shadow-[0_4px_6px_-4px_rgba(0,0,0,0.1),_0_10px_15px_-3px_rgba(0,0,0,0.1)]",
                                'bg-table-map-yellow',
                                { 'w-6 h-6': marker.visitorsCount <= 1 },
                                { 'w-7 h-7': marker.visitorsCount > 1 && marker.visitorsCount <= 5 },
                                { 'w-8 h-8': marker.visitorsCount > 5 && marker.visitorsCount <= 15 },
                                { 'w-9 h-9': marker.visitorsCount > 15 && marker.visitorsCount <= 30 },
                                { 'w-10 h-10': marker.visitorsCount > 30 && marker.visitorsCount <= 50 },
                                { 'w-11 h-11': marker.visitorsCount > 50 && marker.visitorsCount <= 75 },
                                { 'w-12 h-12': marker.visitorsCount > 75 && marker.visitorsCount <= 100 },
                                { 'w-13 h-13': marker.visitorsCount > 100 },
                            )}>
                                <span className="text-xs font-bold text-brand-black">
                                    {marker.visitorsCount}
                                </span>
                            </div>
                        </TagCircleMarker>
                    ))
                }

                {
                    dealerMarkers.map((marker) => (
                        <TagCircleMarker
                            key={`dealer_${marker.id}`}
                            label={`<strong>${marker.label}</strong> `}
                            zIndex={10}
                            position={{
                                lat: marker.position.lat,
                                lng: marker.position.lng
                            }}>
                            <DealerPinIcon height="52" />
                        </TagCircleMarker>
                    ))
                }

            </Map >
        </div>
    )
}
export default MapSide;